import { useEffect, useRef, useState } from 'react';

/**
 * Counts from `start` to `target` once the element scrolls into view.
 * prefix / suffix wrap the number (e.g. "€" + 2 + "B+").
 */
export default function AnimatedCounter({ target, start = 0, prefix = '', suffix = '', duration = 1600, label }) {
  const ref      = useRef(null);
  const [count, setCount] = useState(start);
  const [triggered, setTriggered] = useState(false);

  // Trigger once when element enters viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTriggered(true); observer.disconnect(); } },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // el is captured from ref at mount; IntersectionObserver is a browser global;
    // setTriggered is a stable useState dispatcher
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animate count with ease-out cubic
  useEffect(() => {
    if (!triggered) return;
    const range = target - start;
    let startTime = null;

    function step(ts) {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.round(start + range * eased));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    // eased/progress/range are defined inside this effect; setCount is a stable dispatcher
  }, [triggered, target, start, duration]);

  return (
    <div
      ref={ref}
      className="border border-white/10 p-6 bg-[#111111] hover:border-[#D4A017]/40 transition-colors duration-300 group"
    >
      <div
        className="text-3xl sm:text-4xl font-semibold text-[#D4A017] mb-2 tabular-nums"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {prefix}{count}{suffix}
      </div>
      <div className="text-sm text-white/50">{label}</div>
    </div>
  );
}
